import { DisciplineSubType, DisciplineType } from '@/lib/types';

// params
export const AVAILABLE_DISCIPLINE_TYPE_PARAMS = ['nurse', 'allied'] as const;
export const AVAILABLE_DISCIPLINE_SUB_TYPE_PARAMS = [
  'travel-nurse',
  'travel-allied',
] as const;

// params types
export type DisciplineTypeParam =
  (typeof AVAILABLE_DISCIPLINE_TYPE_PARAMS)[number];
export type DisciplineSubTypeParam =
  (typeof AVAILABLE_DISCIPLINE_SUB_TYPE_PARAMS)[number];

// params to Enums
export const DISCIPLINE_TYPE_PARAM_TO_ENUM: Record<
  DisciplineTypeParam,
  DisciplineType
> = {
  nurse: DisciplineType.Nurse,
  allied: DisciplineType.AlliedHealth,
};

export const DISCIPLINE_SUB_TYPE_PARAM_TO_ENUM: Record<
  DisciplineSubTypeParam,
  DisciplineSubType
> = {
  'travel-nurse': DisciplineSubType.TravelNurse,
  'travel-allied': DisciplineSubType.TravelAllied,
};

// Enums to params
export const DISCIPLINE_TYPE_ENUM_TO_PARAM: Record<
  DisciplineType,
  DisciplineTypeParam
> = {
  [DisciplineType.Nurse]: 'nurse',
  [DisciplineType.AlliedHealth]: 'allied',
};

export const DISCIPLINE_SUB_TYPE_ENUM_TO_PARAM: Record<
  DisciplineSubType,
  DisciplineSubTypeParam
> = {
  [DisciplineSubType.TravelNurse]: 'travel-nurse',
  [DisciplineSubType.TravelAllied]: 'travel-allied',
};

// Enums to Enum
export const DISCIPLINE_TYPE_ENUM_TO_DISCIPLINE_SUB_TYPE_ENUM: Record<
  DisciplineType,
  DisciplineSubType
> = {
  [DisciplineType.Nurse]: DisciplineSubType.TravelNurse,
  [DisciplineType.AlliedHealth]: DisciplineSubType.TravelAllied,
};

export const DISCIPLINE_SUB_TYPE_ENUM_TO_DISCIPLINE_TYPE_ENUM: Record<
  DisciplineSubType,
  DisciplineType
> = {
  [DisciplineSubType.TravelNurse]: DisciplineType.Nurse,
  [DisciplineSubType.TravelAllied]: DisciplineType.AlliedHealth,
};

// Enums to labels
export const DISCIPLINE_TYPE_LABELS: Record<DisciplineType, string> = {
  [DisciplineType.Nurse]: 'Nurse',
  [DisciplineType.AlliedHealth]: 'Allied Health',
};

export const DISCIPLINE_SUB_TYPE_LABELS: Record<DisciplineSubType, string> = {
  [DisciplineSubType.TravelNurse]: 'Travel Nurse',
  [DisciplineSubType.TravelAllied]: 'Travel Allied',
};

// params to labels
export const DISCIPLINE_TYPE_PARAM_TO_TITLE: Record<
  DisciplineTypeParam,
  string
> = {
  nurse: 'Registered Nurse',
  allied: 'Registered Allied',
};

// param validators
export const isParamDisciplineType = (
  data: string | null | undefined
): data is DisciplineTypeParam => {
  return AVAILABLE_DISCIPLINE_TYPE_PARAMS.some(
    (disciplineType) => data === disciplineType
  );
};

export const isParamDisciplineSubType = (
  data: string | null | undefined
): data is DisciplineSubTypeParam => {
  return AVAILABLE_DISCIPLINE_SUB_TYPE_PARAMS.some(
    (disciplineSubType) => data === disciplineSubType
  );
};

// param DisciplineType validators
export const isParamAlliedHealth = (
  data: string | null | undefined
): data is DisciplineTypeParam => {
  return isParamDisciplineType(data) && data === 'allied';
};

export const isParamNurse = (
  data: string | null | undefined
): data is DisciplineTypeParam => {
  return isParamDisciplineType(data) && data === 'nurse';
};

// param DisciplineSubType validators
export const isParamTravelAllied = (
  data: string | null | undefined
): data is DisciplineSubTypeParam => {
  return isParamDisciplineSubType(data) && data === 'travel-allied';
};

export const isParamTravelNurse = (
  data: string | null | undefined
): data is DisciplineSubTypeParam => {
  return isParamDisciplineSubType(data) && data === 'travel-nurse';
};

// DisciplineType validators
export const isAlliedHealth = (
  data: string | null | undefined
): data is DisciplineType => {
  return data === DisciplineType.AlliedHealth;
};

export const isNurse = (
  data: string | null | undefined
): data is DisciplineType => {
  return data === DisciplineType.Nurse;
};

export const isDisciplineType = (
  data: string | null | undefined
): data is DisciplineType => {
  return isAlliedHealth(data) || isNurse(data);
};

// DisciplineSubType validators
export const isTravelAllied = (
  data: string | null | undefined
): data is DisciplineSubType => {
  return data === DisciplineSubType.TravelAllied;
};

export const isTravelNurse = (
  data: string | null | undefined
): data is DisciplineSubType => {
  return data === DisciplineSubType.TravelNurse;
};

export const isDisciplineSubType = (
  data: string | null | undefined
): data is DisciplineSubType => {
  return isTravelAllied(data) || isTravelNurse(data);
};
